function createImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.src = url;
  });
}

export interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

export function getCroppedImageBlob(imageSrc: string, crop: CropArea, mimeType = 'image/jpeg'): Promise<Blob> {
  return createImage(imageSrc).then((image) => {
    const canvas = document.createElement('canvas');
    canvas.width = crop.width;
    canvas.height = crop.height;

    const context = canvas.getContext('2d');
    if (!context) throw new Error('Canvas context unavailable');

    context.drawImage(image, crop.x, crop.y, crop.width, crop.height, 0, 0, crop.width, crop.height);

    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Failed to crop image'));
            return;
          }
          resolve(blob);
        },
        mimeType,
        0.92
      );
    });
  });
}

export function getCropAreaFromTransform(params: {
  imageWidth: number;
  imageHeight: number;
  containerWidth: number;
  containerHeight: number;
  cropWidth: number;
  cropHeight: number;
  zoom: number;
  position: { x: number; y: number };
}): CropArea {
  const { imageWidth, imageHeight, containerWidth, containerHeight, cropWidth, cropHeight, zoom, position } = params;

  const baseScale = Math.max(cropWidth / imageWidth, cropHeight / imageHeight);
  const scale = baseScale * zoom;
  const displayedWidth = imageWidth * scale;
  const displayedHeight = imageHeight * scale;

  const imageLeft = (containerWidth - displayedWidth) / 2 + position.x;
  const imageTop = (containerHeight - displayedHeight) / 2 + position.y;
  const cropLeft = (containerWidth - cropWidth) / 2;
  const cropTop = (containerHeight - cropHeight) / 2;

  const sourceX = (cropLeft - imageLeft) / scale;
  const sourceY = (cropTop - imageTop) / scale;
  const sourceWidth = cropWidth / scale;
  const sourceHeight = cropHeight / scale;

  const x = Math.max(0, Math.min(sourceX, imageWidth));
  const y = Math.max(0, Math.min(sourceY, imageHeight));
  const width = Math.max(1, Math.min(sourceWidth, imageWidth - x));
  const height = Math.max(1, Math.min(sourceHeight, imageHeight - y));

  return { x, y, width, height };
}

/**
 * Redesenha a imagem rotacionada em múltiplos de 90°, trocando largura/altura
 * quando necessário, e devolve um novo data URL — o resultado vira a nova
 * "fonte" para exibição/crop, evitando reescrever a matemática de recorte.
 */
export function rotateImageDataUrl(
  imageSrc: string,
  degrees: 90 | -90,
  mimeType = 'image/jpeg'
): Promise<{ dataUrl: string; width: number; height: number }> {
  return createImage(imageSrc).then((image) => {
    const canvas = document.createElement('canvas');
    canvas.width = image.height;
    canvas.height = image.width;

    const context = canvas.getContext('2d');
    if (!context) throw new Error('Canvas context unavailable');

    context.translate(canvas.width / 2, canvas.height / 2);
    context.rotate((degrees * Math.PI) / 180);
    context.drawImage(image, -image.width / 2, -image.height / 2);

    return {
      dataUrl: canvas.toDataURL(mimeType, 0.95),
      width: canvas.width,
      height: canvas.height,
    };
  });
}
